import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Shield,
  Brain,
  FileText,
  Target,
  Video,
  CheckCircle2,
  ArrowRight,
  Users,
  BarChart3,
  Lock,
  Clock,
  Award,
  Building2,
  Send,
  Loader2,
  Star,
  HeartPulse,
  Menu,
  X,
  FileCheck,
  Eye,
  EyeOff,
  ShieldCheck,
  AlertTriangle,
  FileBarChart,
  ClipboardCheck,
  MessageSquareWarning,
  Phone,
  Mail,
  ChevronRight,
  Sparkles,
  GraduationCap,
  TrendingUp,
  Activity,
  Play
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Brand colors matching the platform
const BRAND = {
  primary: '#1e3a5f',
  teal: '#0d9488',
  light: '#e8f4fc',
  orange: '#f59e0b',
  red: '#ef4444',
  green: '#10b981',
  blue: '#3b82f6'
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: ''
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.email) {
      toast.error('Por favor, preencha nome e e-mail');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });
      } else {
        throw new Error('Erro ao enviar');
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const modules = [
    {
      icon: FileText,
      title: 'PGR - Programa de Gerenciamento de Riscos',
      description: 'Inventário de riscos completo com identificação, avaliação e planos de ação integrados.',
      color: BRAND.teal,
      features: ['Inventário de Riscos', 'Planos de Ação', 'Relatórios PDF']
    },
    {
      icon: Target,
      title: 'AEP - Avaliação Ergonômica Preliminar',
      description: 'Checklist completo baseado na NR-17 com 40 itens de avaliação por domínio.',
      color: BRAND.blue,
      features: ['Checklist NR-17', 'Gráfico Radar', '8 Domínios']
    },
    {
      icon: Brain,
      title: 'COPSOQ II - Avaliações Psicossociais',
      description: 'Questionário validado com 58 perguntas e metodologia semáforo internacional.',
      color: BRAND.green,
      features: ['58 Perguntas', '5 Dimensões', 'Anonimato Total']
    },
    {
      icon: Video,
      title: 'DDS - Diálogo Diário de Segurança',
      description: 'Gestão de treinamentos rápidos com vídeos, presença e certificados automáticos.',
      color: BRAND.orange,
      features: ['Vídeos e Textos', 'Controle de Presença', 'Certificados']
    }
  ];

  const stats = [
    { value: '500+', label: 'Empresas Atendidas', icon: Building2 },
    { value: '50.000+', label: 'Funcionários Protegidos', icon: Users },
    { value: '99.9%', label: 'Uptime Garantido', icon: Activity },
    { value: '24/7', label: 'Suporte Técnico', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] via-white to-[#e8f4fc]">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-slate-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1e3a5f] rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1e3a5f]">CERP Brasil</span>
                <span className="hidden sm:block text-xs text-[#64748b]">Gestão SST Inteligente</span>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('modulos')} className="text-[#64748b] hover:text-[#0d9488] transition-colors text-sm font-medium">
                Módulos
              </button>
              <button onClick={() => scrollToSection('denuncia')} className="text-[#64748b] hover:text-[#0d9488] transition-colors text-sm font-medium">
                Canal de Denúncia
              </button>
              <button onClick={() => scrollToSection('relatorios')} className="text-[#64748b] hover:text-[#0d9488] transition-colors text-sm font-medium">
                Relatórios
              </button>
              <button onClick={() => scrollToSection('contato')} className="text-[#64748b] hover:text-[#0d9488] transition-colors text-sm font-medium">
                Contato
              </button>
              <Link to="/planos" className="text-[#64748b] hover:text-[#0d9488] transition-colors text-sm font-medium">
                Planos
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-[#1e3a5f] hover:text-[#0d9488] hover:bg-[#e8f4fc]"
                onClick={() => navigate('/login')}
              >
                Entrar
              </Button>
              <Button 
                className="bg-[#0d9488] hover:bg-[#0d9488]/90 text-white shadow-lg shadow-[#0d9488]/25"
                onClick={() => navigate('/register')}
              >
                Começar Grátis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-[#1e3a5f]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('modulos')} className="block w-full text-left text-[#64748b] hover:text-[#0d9488] py-2">
                Módulos
              </button>
              <button onClick={() => scrollToSection('denuncia')} className="block w-full text-left text-[#64748b] hover:text-[#0d9488] py-2">
                Canal de Denúncia
              </button>
              <button onClick={() => scrollToSection('relatorios')} className="block w-full text-left text-[#64748b] hover:text-[#0d9488] py-2">
                Relatórios
              </button>
              <button onClick={() => scrollToSection('contato')} className="block w-full text-left text-[#64748b] hover:text-[#0d9488] py-2">
                Contato
              </button>
              <Link to="/planos" className="block text-[#64748b] hover:text-[#0d9488] py-2">
                Planos
              </Link>
              <div className="pt-4 space-y-2 border-t border-slate-100">
                <Button variant="outline" className="w-full border-[#1e3a5f] text-[#1e3a5f]" onClick={() => navigate('/login')}>
                  Entrar
                </Button>
                <Button className="w-full bg-[#0d9488] hover:bg-[#0d9488]/90" onClick={() => navigate('/register')}>
                  Começar Grátis
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#0d9488]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#1e3a5f]/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {/* Badge */}
              <Badge className="bg-[#0d9488]/10 text-[#0d9488] border-[#0d9488]/20 px-4 py-2">
                <Sparkles className="h-4 w-4 mr-2" />
                Nova NR-1 em vigor - Riscos Psicossociais Obrigatórios
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-[#1e3a5f]">Gestão de SST</span>
                <br />
                <span className="text-[#0d9488]">Completa e Inteligente</span>
              </h1>

              <p className="text-lg text-[#64748b] max-w-xl leading-relaxed">
                Plataforma completa para conformidade com a <strong className="text-[#1e3a5f]">NR-1</strong>, 
                incluindo gestão de <strong className="text-[#0d9488]">riscos psicossociais</strong>, 
                PGR, AEP, treinamentos e <strong className="text-[#1e3a5f]">Canal de Denúncia Anônima</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  className="bg-[#0d9488] hover:bg-[#0d9488]/90 text-white text-lg px-8 py-6 shadow-xl shadow-[#0d9488]/25"
                  onClick={() => navigate('/register')}
                >
                  Teste Grátis por 7 Dias
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white text-lg px-8 py-6"
                  onClick={() => scrollToSection('modulos')}
                >
                  Conhecer Módulos
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  <span className="text-sm text-[#64748b]">Sem cartão de crédito</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  <span className="text-sm text-[#64748b]">Suporte em português</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  <span className="text-sm text-[#64748b]">100% NR-1 Compliance</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - Dashboard Preview */}
            <div className="relative hidden lg:block">
              <Card className="bg-white border-slate-200 shadow-2xl overflow-hidden">
                <div className="bg-[#1e3a5f] px-4 py-3 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-4 text-white/60 text-xs">Dashboard CERP Brasil</span>
                </div>
                <CardContent className="p-6 space-y-4">
                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#e8f4fc] rounded-xl p-4 text-center">
                      <HeartPulse className="h-6 w-6 text-[#0d9488] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1e3a5f]">94%</p>
                      <p className="text-xs text-[#64748b]">Saúde Mental</p>
                    </div>
                    <div className="bg-[#e8f4fc] rounded-xl p-4 text-center">
                      <Shield className="h-6 w-6 text-[#10b981] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1e3a5f]">100%</p>
                      <p className="text-xs text-[#64748b]">Conformidade</p>
                    </div>
                    <div className="bg-[#e8f4fc] rounded-xl p-4 text-center">
                      <TrendingUp className="h-6 w-6 text-[#3b82f6] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-[#1e3a5f]">+45%</p>
                      <p className="text-xs text-[#64748b]">Engajamento</p>
                    </div>
                  </div>
                  
                  {/* Progress Bars */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#64748b]">PGR Atualizado</span>
                        <span className="text-[#10b981] font-medium">100%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#64748b]">Avaliações Psicossociais</span>
                        <span className="text-[#0d9488] font-medium">87%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-[87%] bg-gradient-to-r from-[#0d9488] to-[#3b82f6] rounded-full" />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#64748b]">Treinamentos DDS</span>
                        <span className="text-[#f59e0b] font-medium">92%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-[92%] bg-gradient-to-r from-[#f59e0b] to-[#ef4444] rounded-full" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-[#10b981] rounded-xl p-3 shadow-lg shadow-[#10b981]/25 animate-bounce">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#1e3a5f] rounded-xl p-3 shadow-lg">
                <Brain className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/20 mb-4">
                <Play className="h-4 w-4 mr-2" />
                Veja o CERP Brasil em Ação
              </Badge>
              <h3 className="text-2xl font-bold text-[#1e3a5f]">
                Conheça nossa plataforma em <span className="text-[#0d9488]">2 minutos</span>
              </h3>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/6rQM2kE31zk?rel=0&modestbranding=1"
                  title="CERP Brasil - Plataforma de Gestão SST"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-[#e8f4fc] rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="h-6 w-6 text-[#0d9488]" />
                </div>
                <p className="text-3xl md:text-4xl font-bold text-[#1e3a5f]">{stat.value}</p>
                <p className="text-[#64748b] text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modulos" className="py-20 bg-gradient-to-b from-white to-[#e8f4fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/20 mb-4">
              Módulos Completos
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Tudo para a <span className="text-[#0d9488]">NR-1</span> em um só lugar
            </h2>
            <p className="text-[#64748b] max-w-2xl mx-auto">
              Plataforma integrada com todos os módulos para gestão completa de Saúde e Segurança do Trabalho
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((module, index) => (
              <Card 
                key={index} 
                className="bg-white border-slate-200 hover:border-[#0d9488]/50 hover:shadow-xl transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${module.color}15` }}
                    >
                      <module.icon className="h-7 w-7" style={{ color: module.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2 group-hover:text-[#0d9488] transition-colors">
                        {module.title}
                      </h3>
                      <p className="text-[#64748b] text-sm mb-4">
                        {module.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {module.features.map((feature, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-[#e8f4fc] rounded-full text-xs text-[#1e3a5f] font-medium"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Canal de Denúncia Section */}
      <section id="denuncia" className="py-20 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-white/10 text-white border-white/20">
                <Lock className="h-4 w-4 mr-2" />
                Canal de Denúncia Anônima
              </Badge>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Sigilo Total e <span className="text-[#0d9488]">Segurança Garantida</span>
              </h2>
              
              <p className="text-white/80 text-lg leading-relaxed">
                Ofereça um canal seguro para seus colaboradores reportarem irregularidades, 
                assédio moral, riscos à segurança e outras situações de forma <strong>100% anônima</strong>.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0d9488] rounded-lg flex items-center justify-center shrink-0">
                    <EyeOff className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Anonimato Garantido</h4>
                    <p className="text-white/60 text-sm">Denunciante não precisa se identificar. Sem rastreamento.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0d9488] rounded-lg flex items-center justify-center shrink-0">
                    <ShieldCheck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Protocolo de Acompanhamento</h4>
                    <p className="text-white/60 text-sm">Código único para acompanhar status sem revelar identidade.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0d9488] rounded-lg flex items-center justify-center shrink-0">
                    <FileBarChart className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Relatórios Gerenciais</h4>
                    <p className="text-white/60 text-sm">Dashboard com métricas, categorias e tempo de resposta.</p>
                  </div>
                </div>
              </div>

              <Button 
                size="lg"
                className="bg-[#0d9488] hover:bg-[#0d9488]/90 text-white mt-4"
                onClick={() => navigate('/register')}
              >
                Implementar Canal de Denúncia
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            {/* Visual */}
            <div className="relative">
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#0d9488] rounded-xl flex items-center justify-center">
                      <MessageSquareWarning className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold">Canal de Denúncia</h4>
                      <p className="text-white/60 text-sm">Sua Empresa LTDA</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-white/40 text-xs mb-2">CATEGORIA</p>
                      <p className="text-white text-sm">Assédio Moral</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4">
                      <p className="text-white/40 text-xs mb-2">DESCRIÇÃO</p>
                      <p className="text-white/80 text-sm">Sua denúncia será tratada com total sigilo...</p>
                    </div>
                  </div>
                  
                  <div className="bg-[#0d9488]/20 rounded-lg p-4 text-center">
                    <Lock className="h-6 w-6 text-[#0d9488] mx-auto mb-2" />
                    <p className="text-white text-sm font-medium">Conexão Criptografada</p>
                    <p className="text-white/60 text-xs">Seus dados estão protegidos</p>
                  </div>
                </CardContent>
              </Card>
              
              {/* QR Code visual */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl">
                <div className="w-20 h-20 bg-[#e8f4fc] rounded-lg flex items-center justify-center">
                  <div className="grid grid-cols-5 gap-1">
                    {[...Array(25)].map((_, i) => (
                      <div key={i} className={`w-3 h-3 rounded-sm ${Math.random() > 0.5 ? 'bg-[#1e3a5f]' : 'bg-transparent'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-[#64748b] text-xs text-center mt-2">QR Code Único</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Relatórios para Fiscalização Section */}
      <section id="relatorios" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Visual */}
            <div className="order-2 lg:order-1">
              <Card className="bg-[#e8f4fc] border-0 shadow-xl overflow-hidden">
                <div className="bg-[#1e3a5f] px-4 py-3 flex items-center justify-between">
                  <span className="text-white text-sm font-medium">Relatório PGR - Pronto para Fiscalização</span>
                  <Badge className="bg-[#10b981] text-white border-0">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Validado
                  </Badge>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#0d9488]" />
                      <span className="text-[#1e3a5f] font-medium">Inventário de Riscos</span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <ClipboardCheck className="h-5 w-5 text-[#0d9488]" />
                      <span className="text-[#1e3a5f] font-medium">Planos de Ação</span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <Brain className="h-5 w-5 text-[#0d9488]" />
                      <span className="text-[#1e3a5f] font-medium">Avaliação Psicossocial</span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <Target className="h-5 w-5 text-[#0d9488]" />
                      <span className="text-[#1e3a5f] font-medium">AEP - Ergonomia</span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-5 w-5 text-[#0d9488]" />
                      <span className="text-[#1e3a5f] font-medium">Certificados de Treinamento</span>
                    </div>
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  </div>
                  
                  <Button className="w-full bg-[#0d9488] hover:bg-[#0d9488]/90 text-white mt-4">
                    <FileBarChart className="h-4 w-4 mr-2" />
                    Exportar PDF Completo
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6 order-1 lg:order-2">
              <Badge className="bg-[#10b981]/10 text-[#10b981] border-[#10b981]/20">
                <FileCheck className="h-4 w-4 mr-2" />
                Relatórios para Fiscalização
              </Badge>
              
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f]">
                Pronto para qualquer <span className="text-[#0d9488]">Auditoria</span>
              </h2>
              
              <p className="text-[#64748b] text-lg leading-relaxed">
                Gere relatórios completos e organizados que atendem 100% às exigências da 
                <strong className="text-[#1e3a5f]"> NR-1</strong> e do <strong className="text-[#1e3a5f]">eSocial</strong>. 
                Esteja sempre preparado para fiscalizações do MTE.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#e8f4fc] rounded-xl p-4">
                  <h4 className="text-[#1e3a5f] font-bold text-2xl">PDF</h4>
                  <p className="text-[#64748b] text-sm">Relatórios prontos para impressão</p>
                </div>
                <div className="bg-[#e8f4fc] rounded-xl p-4">
                  <h4 className="text-[#1e3a5f] font-bold text-2xl">Excel</h4>
                  <p className="text-[#64748b] text-sm">Dados para análise avançada</p>
                </div>
                <div className="bg-[#e8f4fc] rounded-xl p-4">
                  <h4 className="text-[#1e3a5f] font-bold text-2xl">eSocial</h4>
                  <p className="text-[#64748b] text-sm">Integração com envio automático</p>
                </div>
                <div className="bg-[#e8f4fc] rounded-xl p-4">
                  <h4 className="text-[#1e3a5f] font-bold text-2xl">Histórico</h4>
                  <p className="text-[#64748b] text-sm">Rastreabilidade completa</p>
                </div>
              </div>

              <Button 
                size="lg"
                className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white"
                onClick={() => navigate('/register')}
              >
                Começar a Usar
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#e8f4fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-[#0d9488]/10 text-[#0d9488] border-[#0d9488]/20 mb-4">
              Benefícios
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Por que escolher o <span className="text-[#0d9488]">CERP Brasil?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Conformidade Total NR-1', description: 'Sistema desenvolvido para atender 100% das exigências incluindo riscos psicossociais.' },
              { icon: Clock, title: 'Economia de Tempo', description: 'Reduza em até 80% o tempo gasto com documentação e relatórios de SST.' },
              { icon: Lock, title: 'Dados Seguros', description: 'Criptografia de ponta a ponta e anonimato garantido nas avaliações.' },
              { icon: BarChart3, title: 'Dashboards em Tempo Real', description: 'Visualize métricas e indicadores de saúde ocupacional em painéis interativos.' },
              { icon: Building2, title: 'Multi-Empresa', description: 'Gerencie múltiplos CNPJs em uma única plataforma com consolidação de dados.' },
              { icon: Award, title: 'Certificados Automáticos', description: 'Gere certificados de treinamento com validade e rastreabilidade completa.' }
            ].map((benefit, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#0d9488]/10 flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-[#0d9488]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{benefit.title}</h3>
                  <p className="text-[#64748b] text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1e3a5f] to-[#0d9488]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Pronto para transformar a gestão de SST da sua empresa?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Comece agora com 7 dias grátis. Sem compromisso, sem cartão de crédito.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-[#1e3a5f] hover:bg-white/90 text-lg px-10 py-6 shadow-xl"
              onClick={() => navigate('/register')}
            >
              Começar Teste Grátis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-10 py-6"
              onClick={() => navigate('/planos')}
            >
              Ver Planos e Preços
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f] border-[#1e3a5f]/20 mb-4">
                Contato
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
                Fale com nosso <span className="text-[#0d9488]">time comercial</span>
              </h2>
              <p className="text-[#64748b] mb-8">
                Tire suas dúvidas, solicite uma demonstração personalizada ou receba uma proposta sob medida para sua empresa.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#e8f4fc] flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[#0d9488]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748b]">E-mail</p>
                    <p className="text-[#1e3a5f] font-medium">comercial@cerpbrasil.com.br</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#e8f4fc] flex items-center justify-center">
                    <Phone className="h-5 w-5 text-[#0d9488]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748b]">WhatsApp</p>
                    <p className="text-[#1e3a5f] font-medium">Clique no botão flutuante</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#e8f4fc] flex items-center justify-center">
                    <Clock className="h-5 w-5 text-[#0d9488]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748b]">Atendimento</p>
                    <p className="text-[#1e3a5f] font-medium">Segunda a Sexta, 8h às 18h</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-[#e8f4fc] border-0 shadow-xl">
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#1e3a5f] font-medium mb-1 block">Nome *</label>
                      <Input
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        placeholder="Seu nome"
                        className="bg-white border-slate-200 focus:border-[#0d9488]"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#1e3a5f] font-medium mb-1 block">E-mail *</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="seu@email.com"
                        className="bg-white border-slate-200 focus:border-[#0d9488]"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-[#1e3a5f] font-medium mb-1 block">Telefone</label>
                      <Input
                        value={formData.telefone}
                        onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                        placeholder="(00) 00000-0000"
                        className="bg-white border-slate-200 focus:border-[#0d9488]"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#1e3a5f] font-medium mb-1 block">Empresa</label>
                      <Input
                        value={formData.empresa}
                        onChange={(e) => setFormData({...formData, empresa: e.target.value})}
                        placeholder="Nome da empresa"
                        className="bg-white border-slate-200 focus:border-[#0d9488]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-[#1e3a5f] font-medium mb-1 block">Mensagem</label>
                    <Textarea
                      value={formData.mensagem}
                      onChange={(e) => setFormData({...formData, mensagem: e.target.value})}
                      placeholder="Como podemos ajudar?"
                      rows={4}
                      className="bg-white border-slate-200 focus:border-[#0d9488] resize-none"
                    />
                  </div>
                  <Button 
                    type="submit"
                    className="w-full bg-[#0d9488] hover:bg-[#0d9488]/90 text-white py-6"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a5f] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-[#1e3a5f]" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">CERP Brasil</span>
                <p className="text-white/60 text-xs">Gestão SST Inteligente</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/termos-de-uso" className="hover:text-white transition-colors">Termos de Uso</Link>
              <Link to="/politica-de-privacidade" className="hover:text-white transition-colors">Privacidade</Link>
              <button onClick={() => scrollToSection('contato')} className="hover:text-white transition-colors">Contato</button>
            </div>
            
            <p className="text-sm text-white/40">
              © 2026 CERP Brasil. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5511999999999?text=Olá! Gostaria de saber mais sobre o CERP Brasil."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default LandingPage;
